/**
 * ReviewDashboardPresenter
 * --
 */
import React, { useRef, useState, useEffect } from 'react';
import { Content, Title, Row, Col } from 'components';
import {
  Button,
  DatePicker,
  Divider,
  Input,
  Table,
  Radio,
  Spin,
  Statistic,
} from 'antd';
import {
  // XAxis,
  // YAxis,
  // BarChart,
  // Bar,
  // Legend,
  CartesianGrid,
  // ComposedChart,
  Tooltip,
  Cell,
  BarChart,
  Pie,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Bar,
  Rectangle,
} from 'recharts';
import { ExportOutlined } from '@ant-design/icons';
import { stringToMoneyFormat } from 'utils';

/**----------------------------------------
 * [Component] 총관리자: 장바구니 분석 - 프레젠터
 * ----------------------------------------
 */
const ReviewDashboardPresenter = ({ loading, reviewData, reviewList }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  useEffect(() => {
    const call = (e) => {
      setChartWidth(chartRef.current.clientWidth);
    };
    if (chartRef.current) call();
    window.addEventListener('resize', call);
    return () => {
      window.removeEventListener('resize', call);
    };
  }, [chartRef]);

  /* ===== VARIABLE ===== */
  // 리뷰 수
  const reviewCount = reviewData
    ? reviewData.reduce((acc, cur) => acc + Number(cur.review_count), 0)
    : 0;
  // 평균점수
  const avgScore =
    reviewData && reviewData.length > 0
      ? reviewData.reduce(
          (acc, cur) => acc + parseFloat(cur?.avg_score ?? 0),
          0
        ) / reviewData?.length ?? 0
      : 0;

  // 상품수
  const productCount = reviewData ? reviewData.length : 0;
  // 답글없는 리뷰 수
  const waitList = !reviewList
    ? []
    : reviewList.filter((item) => item.is_show === null);
  // console.log('waitList: ', waitList);

  /* ===== RENDER ===== */
  // console.log('reviewData: ', reviewData);
  // JSX: start
  return (
    <Spin spinning={loading}>
      <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
        <Row>
          <Col x={24}>
            <Title size={4} style={{ marginBottom: 12 }}>
              리뷰 대시보드
            </Title>

            {/* 요약 */}
            <Content
              fullWidth
              padding={0}
              backgroundColor={'none'}
              style={{
                marginBottom: 5,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {[
                {
                  title: '리뷰수',
                  value: stringToMoneyFormat(reviewCount ?? 0),
                  unit: '개',
                  link: '/review/list',
                },
                {
                  title: '상품수',
                  value: stringToMoneyFormat(productCount ?? 0),
                  unit: '개',
                  link: '/product/list',
                },
                {
                  title: '전체평점',
                  value: avgScore?.toFixed(1) ?? 0,
                  unit: '점',
                  link: '/review/list',
                },
                {
                  title: '승인대기',
                  value: stringToMoneyFormat(waitList?.length ?? 0),
                  unit: '점',
                  link: '/review/list',
                },
              ].map((_item, _index) => (
                <div
                  key={`${_item.label}-${_index}`}
                  className="boxShadow"
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 4,
                    width: `90%`,
                    margin: '0 2px',
                    padding: '16px 15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Statistic
                    className="cursorOverOpacity"
                    direction={'row'}
                    size="large"
                    title={_item.title}
                    value={_item.value}
                    suffix={_item.suffix}
                    // style={{ justifyContent: 'center' }}
                    valueStyle={{
                      fontSize: '1.45em',
                      color: '#1677ff',
                      // textDecoration: 'underline !important',
                    }}
                  />
                  <div>
                    <Button
                      shape="circle"
                      onClick={() => window.open(_item.link, '_blank')}
                    >
                      <ExportOutlined />
                    </Button>
                  </div>
                </div>
              ))}
            </Content>

            {/* 요약 끝 */}

            {/* ===== 차트: start ===== */}
            <Row gutter={[5, 5]}>
              {/* === 상품별: start === */}
              <Col x={24}>
                <Content
                  maxWidth={'100%'}
                  borderRadius={4}
                  padding={'14px 5px'}
                >
                  <Title
                    size={5}
                    align="center"
                    style={{ padding: '14px 20px' }}
                  >
                    상품별 평점
                  </Title>
                  <Divider style={{ margin: 0 }} />
                  <ResponsiveContainer width="100%" height={450}>
                    <BarChart
                      width={500}
                      // layout="vertical"
                      data={reviewData?.map((item) => ({
                        ...item,
                        ['평점']:
                          parseFloat(item.avg_score) >= 0
                            ? parseFloat(item.avg_score)
                            : 0,
                      }))}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <YAxis type="number" />
                      <XAxis type="category" dataKey="product_name" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="평점" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </Content>
              </Col>
              {/* === 상품별: end === */}
            </Row>
            {/* ===== 차트: end ===== */}

            {/* ===== Raw 데이터: start ===== */}
            <Row gutter={[5, 5]}>
              <Col x={24}>
                <Content
                  maxWidth={'100%'}
                  padding={20}
                  // backgroundColor={'none'}
                  style={{ margin: '5px 0' }}
                >
                  <Title size={5} style={{ marginBottom: 10 }}>
                    상품별 순위
                  </Title>
                  <Table
                    bordered
                    size="small"
                    dataSource={reviewData ?? []}
                    columns={[
                      {
                        title: '상품명',
                        dataIndex: 'product_name',
                      },
                      {
                        title: '카테고리명',
                        dataIndex: 'product_category_name',
                        width: 350,
                        align: 'center',
                      },
                      {
                        title: '리뷰 수',
                        dataIndex: 'review_count',
                        width: 170,
                        align: 'center',
                        render: (value) => `${stringToMoneyFormat(value)}개`,
                      },
                      {
                        title: '베스트리뷰 수',
                        dataIndex: 'best_review_count',
                        width: 170,
                        align: 'center',
                        render: (value) => `${stringToMoneyFormat(value)}개`,
                      },
                      {
                        title: '평점',
                        dataIndex: 'avg_score',
                        width: 150,
                        align: 'center',
                        render: (value) => `${Number(value).toFixed()}점`,
                      },
                    ]}
                  />
                </Content>
              </Col>
            </Row>
            {/* ===== Raw 데이터: end ===== */}
          </Col>
        </Row>
      </Content>
    </Spin>
  );
};

export default ReviewDashboardPresenter;
